import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Panel } from "@/components/common/Panel";
import { useApp } from "@/context/AppContext";
import { formatDate } from "@/lib/format";
import { Lock, Unlock, Archive } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const err = (e, f) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || f; };

export function GovernancePanel({ matter }) {
  const { can } = useApp();
  const [g, setG] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => axios.get(`${API}/matters/${matter.id}/governance`).then((r) => setG(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [matter.id]);
  if (!g) return <Panel className="p-4 text-sm text-ink-muted">Loading governance…</Panel>;
  const held = g.legalHold?.active;
  const manage = can("matter.close");

  const toggleHold = async () => {
    try {
      await axios.post(`${API}/matters/${matter.id}/legal-hold`, { active: !held, reason });
      toast.success(held ? "Legal hold released." : "Legal hold placed. Purge and deletion are frozen."); setReason(""); load();
    } catch (e) { toast.error(err(e, "Could not update legal hold.")); }
  };
  const setPolicy = async (policy) => {
    try { await axios.put(`${API}/matters/${matter.id}/retention`, { policy }); toast.success("Retention policy updated."); load(); }
    catch (e) { toast.error(err(e, "Could not update retention.")); }
  };

  return (
    <div className="space-y-4" data-testid="governance-panel">
      <Panel className={`p-4 ${held ? "border-danger/40 bg-danger-bg" : ""}`}>
        <div className="flex items-center gap-2 font-display text-base font-semibold text-ink">{held ? <Lock className="h-4 w-4 text-danger" /> : <Unlock className="h-4 w-4 text-ink-2" />} Legal Hold</div>
        <div className="mt-1 text-xs text-ink-2" data-testid="legal-hold-status">
          {held ? <>Active since {formatDate(g.legalHold.at)} · {g.heldDocuments} document(s) frozen<br /><span className="text-ink">Reason: {g.legalHold.reason}</span></> : "No hold. Records follow the normal 30-day trash and retention cycle."}
        </div>
        {manage && (
          <div className="mt-3 space-y-2">
            {!held && <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required) — e.g. litigation anticipated, regulator notice" data-testid="legal-hold-reason" />}
            <Button size="sm" variant={held ? "outline" : "default"} className={held ? "" : "bg-danger hover:bg-danger/90"} onClick={toggleHold} data-testid="legal-hold-toggle">
              {held ? "Release Hold" : "Place Legal Hold"}
            </Button>
          </div>
        )}
      </Panel>
      <Panel className="p-4">
        <div className="flex items-center gap-2 font-display text-base font-semibold text-ink"><Archive className="h-4 w-4 text-copper" /> Retention</div>
        <div className="mt-1 text-xs text-ink-2">Source: {g.retentionSource} · retain until <b data-testid="retention-until">{g.retentionUntil?.startsWith("9999") ? "permanent" : formatDate(g.retentionUntil)}</b>{g.closedAt ? ` (closed ${formatDate(g.closedAt)})` : " (counted from closure)"}</div>
        <select className="mt-3 h-9 w-full rounded-md border border-line bg-card px-2 text-sm" value={g.retentionPolicy} disabled={!manage} onChange={(e) => setPolicy(e.target.value)} data-testid="retention-policy-select">
          {g.policies.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </Panel>
    </div>
  );
}
